import React from 'react';

import { useMe } from '$/hooks/useMe';
import { hasPermission } from '$/utils/hasPermission';

import { usePermissionModal } from '../Permission';
import { useDirtyFormContext } from './DirtyFormContext';

export type LinkHasPermissionOptions = {
  disabled?: boolean;
  skipDirtyForm?: boolean;
  askForPublish?: boolean;
};

export type LinkHasPermission = (to: string, options?: LinkHasPermissionOptions) => boolean;

export const useLinkHasPermission = (): {
  linkHasPermission: LinkHasPermission;
  linkRef: React.RefObject<HTMLAnchorElement>;
} => {
  const me = useMe();

  // handle show permission modal
  const { showPermissionModal } = usePermissionModal();

  // dirty form context
  const { forms, toggleUnsavedChangesModal } = useDirtyFormContext();

  // link ref
  const linkRef = React.useRef<HTMLAnchorElement>(null);

  const linkHasPermission: LinkHasPermission = React.useCallback(
    (to, options) => {
      if (options?.disabled) {
        return false;
      }

      if (!hasPermission(to, me)) {
        // check permission
        showPermissionModal && showPermissionModal(to);
        return false;
      }

      // is any active form dirty
      const dirtyForm = Object.entries(forms).find(
        ([_formId, formState]) => formState.isDirty === true
      );

      // check if there is any dirty form
      if (!options?.skipDirtyForm && dirtyForm && dirtyForm[1].isDirty === true) {
        // prevent navigation if dirty form
        toggleUnsavedChangesModal({
          dirtyForm: dirtyForm[1],
          triggerNavigation: () => {
            if (linkRef && linkRef.current) {
              linkRef.current.click();
            }
          },
          onClose: () => {
            toggleUnsavedChangesModal();
          },
        });

        return false;
      }

      return true;
    },
    [forms, me, showPermissionModal, toggleUnsavedChangesModal]
  );

  return { linkHasPermission, linkRef };
};
